import {
  Paper,
  TableContainer,
  useTheme,
  alpha,
  colors,
  type SxProps,
} from "@mui/material";
import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import invariant from "tiny-invariant";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { FlexColumn } from "../layout/Flex";
import type { DraggedItem } from "../dragndrop/types";

interface DroppableContainerProps {
  styles?: SxProps;
  emptyDroppableTableData?: DraggedItem;
}

export const DroppableContainer = ({
  styles,
  emptyDroppableTableData,
  children,
}: PropsWithChildren<DroppableContainerProps>) => {
  const theme = useTheme();
  const ref = useRef(null);

  const [isDragged, setIsDragged] = useState(false);

  useEffect(() => {
    if (!emptyDroppableTableData) return;
    const tableEl = ref.current;
    invariant(tableEl, "Section component should exist");
    return dropTargetForElements({
      element: tableEl,
      onDragEnter: () => setIsDragged(true),
      onDragLeave: () => setIsDragged(false),
      onDrop: () => setIsDragged(false),
      getData: () => emptyDroppableTableData,
      getIsSticky: () => true,
    });
  }, [emptyDroppableTableData]);

  return (
    <FlexColumn>
      <TableContainer
        ref={ref}
        component={Paper}
        sx={{
          ...styles,
          flexGrow: 1,
          backgroundColor: isDragged
            ? `${alpha(
                theme.palette.primary.main,
                theme.palette.action.selectedOpacity
              )}`
            : colors.common.white,
        }}
      >
        {children}
      </TableContainer>
    </FlexColumn>
  );
};
